<script setup lang="ts">
import { watch, ref, computed } from 'vue';
import { InputNullableInt } from '@/components';
import { useHelper } from '@/composeables';
import { isCommonKey } from '@/utilities/keyboardutils';

/**
 * @file InputCode.vue
 * @module InputCode
 * @description Component handling an input code that we use to validate for example a phone number, email or similar.
 */

defineOptions({ inheritAttrs: false });

const props = withDefaults(
    defineProps<{
        /**
         * The value of the input
         */
        modelValue: string;

        /**
         * The number of inputs to show
         * @default 6
         */
        numberOfInputs?: number;
    }>(),
    {
        /**
         * The number of inputs to show
         */
        numberOfInputs: 6,
    },
);

/**
 * The emits available
 */
const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void;

    /**
     * When the code is completed.
     */
    (e: 'completed'): void;

    /**
     * When the form is submitted. This is when a code is filled in and the user presses enter.
     */
    (e: 'submit'): void;
}>();

/**
 * The internal model values of the input
 * we need to fill this with null values to make sure that the input is empty
 */
const modelValues = ref<Array<number | null>>(Array.from({ length: props.numberOfInputs }, () => null));

/**
 * A list of all the inputs
 * We use this one to easily focus the next input when a key is pressed
 */
const inputs = ref<Array<InstanceType<typeof InputNullableInt>>>([]);

const onKeyUp = (e: KeyboardEvent, index: number) => {
    let nextIndex = 0;
    if (e.key === 'Backspace') {
        nextIndex = index - 1 < 0 ? 0 : index - 1;
    } else if (/* only accept e.key as number*/ e.key.match(/[0-9]/)) {
        nextIndex = index + 1 > modelValues.value.length ? 0 : index + 1;
    }

    if (e.key === 'Backspace' || !isCommonKey(e)) {
        if (e.key.match(/[0-9]/)) {
            modelValues.value[index] = parseInt(e.key); // we updtate the model value with the key. This make sure we can only have 1 digit in each input
            inputs.value[nextIndex]?.focus();
        } else if (e.key === 'Backspace') {
            inputs.value[nextIndex]?.focus();
        }

        return useHelper().stopPropagation(e);
    }

    let valueLengthWithoutNull = modelValues.value.filter((v) => v !== null).length;
    if (e.key === 'Enter' && valueLengthWithoutNull == props.numberOfInputs) {
        emit('submit');
        return;
    }

    if (e.key == 'Tab') {
        return false;
    }

    return useHelper().stopPropagation(e);
};

/**
 * We stop the keyDown event to cancel all input from keyboard and handle the input in the onKeyUp event when we know the key is valid
 * @param e
 * @param index
 */
const onKeyDown = (e: KeyboardEvent, index: number) => {
    return useHelper().stopPropagation(e);
};

/**
 * The internal value of the input
 */
const value = computed({
    get: () => props.modelValue,
    set: (val: string) => emit('update:modelValue', val),
});

/**
 * This is used to set the correct value of the component
 * and emit the correct value via the computed property
 */
watch(
    () => modelValues.value,
    (val) => (value.value = val.join('')),
    { deep: true },
);

/**
 * We autosubmit the form when the code is filled
 */
watch(value, (val) => {
    if (val.length == props.numberOfInputs) {
        emit('completed');
    }
});

let inputTimerId: number | null = null;

/**
 * This is an event handler for the input event. This is triggered every time the input is changed
 * We use this one only to populate the model value with the correct values if the value is the correct length
 *
 * We use this one to auto fill the inputs on mobile devices that receive the code via SMS or similar.
 *
 * @remarks This is debounced to make sure that we only get the last value since it is fired on every character
 * @param event
 */
const onInput = (event: InputEvent) => {
    // We debounce the input for 10 ms to make sure that we only get the last value
    if (inputTimerId) {
        window.clearTimeout(inputTimerId);
    }

    // TODO: Why is there a setTimeout here?
    inputTimerId = window.setTimeout(() => {
        if (!event.target) {
            return;
        }
        if (!(event.target instanceof HTMLInputElement)) {
            return;
        }
        // We need to make sure that the value is always a string
        let value = event.target.value.toString();

        inputTimerId = null;
        if (value.length >= props.numberOfInputs) {
            populateModelValue(value);
        }
    }, 10);
    return useHelper().stopPropagation(event);
};

/**
 * Populate the model value with the value from the input
 * @param value
 */
const populateModelValue = (value: string) => {
    // We validate each value and make sure they are integers and skip all that is not integers
    for (let i = 0; i < value.length; i++) {
        if (!value[i].match(/[0-9]/)) {
            value = value.replace(value[i], '');
        }
    }

    // we map one and one to the modelValue array
    for (let i = 0; i < Math.min(value.length, props.numberOfInputs); i++) {
        // Add the values to the inputs
        if (value[i] !== '') {
            modelValues.value[i] = parseInt(value[i]);
        }
    }
};

const onPaste = (e: ClipboardEvent) => {
    const pastedData = e.clipboardData?.getData('text');
    if (pastedData) {
        const pastedValues = pastedData.split('');
        modelValues.value = pastedValues.map((v) => (v === '' ? null : parseInt(v)));
    }
    return useHelper().stopPropagation(e);
};
</script>

<template>
    <span class="form-control-inputcode">
        <InputNullableInt
            v-for="(item, i) in numberOfInputs"
            :key="i"
            ref="inputs"
            v-model="modelValues[i]"
            placeholder="0"
            :class="$attrs.class"
            @keyup="onKeyUp($event, i)"
            @keydown="onKeyDown($event, i)"
            @input="onInput($event)"
            @paste="onPaste"
        />
    </span>
</template>
